/**
 * Heat Loss Calculator
 * ===
 * Radiator Suggestions View
 * 
 * Extends Backbone View.
 *
 * Author: Duncan McMillan
 *
 * Copyright (c) August 2024 · Art & Soul Communications Ltd.
 * https://artandsoul.co.uk
 *
 */

'use strict';

import _ from 'underscore';
import Backbone from 'backbone';

import { HeatLossCalculator } from './../HeatLossCalculator.Object.js';
import { HLC_Configs } from './../HeatLossCalculator.Configs.js';
import { HLC_Utils } from './../HeatLossCalculator.Utils.js';

/**
 * Radiator Suggestions View
 *
 */
export var RadiatorSuggestionsView = Backbone.View.extend({
    
    events: {
        'change input[name="radiator_combination"]' : 'onCombinationSelect',
        'click .hlc-radiators__remove' : 'onClickRemove'
    },
    
    tagName: 'section',
    className: 'hlc-section hlc-radiators-pane is-inactive',
    
    template: undefined,
    
    suggestions: undefined,
    rejections: undefined,
    
    radiator_combination: '100',
    
    initialize: function(options) {
        
        var that = this;
        
        this.template = _.template(options.template);
        this.suggestions = new Backbone.Collection(null, {
            comparator: function(radiator_1, radiator_2) {
                // Ensures collection is sorted in ascending order of output value
                switch (true) {
                    case radiator_1.get('output') > radiator_2.get('output') :
                        return 1;
                        break;
                    case radiator_1.get('output') < radiator_2.get('output') :
                        return -1;
                        break;
                    default :
                        return 0;
                }
            }
        });
        
        this.rejections = new Backbone.Collection();
        
        return this;
    },
    
    activate: function() {
        
        var that = this;
        
        this.$el.find('.hlc-radiators').slideDown({
            start: function () {
                $(this).css({
                    display: 'flex'
                });
            } /*,
            complete: function() {
                $('html, body').animate({
                    scrollTop: that.$el.offset().top
                });
            } */
        });
        this.$el.removeClass('is-inactive').addClass('is-active');
    },
    
    onCombinationSelect: function(e) {
        
        this.radiator_combination = $(e.currentTarget).val();
        
        return this.render();
    },
    
    onClickRemove: function(e) {
        
        e.preventDefault();
        
        var id, radiator;
        
        id = $(e.currentTarget).parent().data('radiator-id');
        
        this.rejections.add(this.suggestions.remove(id));
        
        $(e.currentTarget).parent().fadeOut({
            complete:function() {
                this.remove();
            }
        });
        
        return this;
    },
    
    suggestRadiators: function() {
        
        var that, required_output, min, max, rad_split, coolest, delta_t;
        
        that = this;
        
        switch(this.radiator_combination) {
            
            case '50|50' :
                rad_split = 0.5;
                break;
                
            default :
                rad_split = 1;
        }
        
        required_output = this.model.totalHeatLoss() * rad_split;
        
        delta_t = this.model.deltaT();
        
        this.suggestions.reset([]);
        
        HeatLossCalculator.app.preferred_ranges.each(function(range) {
            
            if (range.radiators.length) {
                
                var candidates, out_of_range_rads;
                
                candidates = [];
                
                // Find the coolest radiator in the range
                coolest = range.radiators.min(function(radiator) {
                    return Number(radiator.get('output'));
                });
                
                // We wish to select radiators whose output falls within a range of values.
                // The lower (min) value is the heat loss requirement.
                // The upper (max) value is the heat loss requirement mult by the tolerance defined in configs.
                
                // If the coolest radiator in the range provides more than the requirement * tolerance then
                // we’ll use its value as our minimum, otherwise we’ll use the requirement as the minimum. 
                if (required_output < coolest.getOutputCorrectedFor(delta_t)) {
                    min = coolest.getOutputCorrectedFor(delta_t);
                } else {
                    min = required_output;
                }
                
                // The maximum value is just the minimum * tolerance.
                max = min * HLC_Configs.heatloss_tolerance_factor;
                
                // Any radiators whose output fall within the min and max values will be added to the candiates
                candidates = range.radiators.filter(function(radiator) {
                    return radiator.getOutputCorrectedFor(delta_t) >= min && radiator.getOutputCorrectedFor(delta_t) <= max && that.rejections.indexOf(radiator) === -1;
                });
                
                if ( ! candidates.length ) {
                    // No radiators between min and max so let’s grab any that are out of (above) range…
                    out_of_range_rads = range.radiators.filter(function(radiator) {
                        return radiator.getOutputCorrectedFor(delta_t) > max;
                    });
                    
                    // …then figure out what the lowest output value is that is out of range
                    if (out_of_range_rads.length) {
                        max = Number(_.first(out_of_range_rads).get('output'));
                        
                        candidates = range.radiators.filter(function(radiator) {
                            return radiator.getOutputCorrectedFor(delta_t) == max;
                        });
                    }
                }
                
                that.suggestions.add(candidates);
            }
        });
        
        return this;
    },
    
    reinstateRejects: function() {
        this.rejections.reset();
        return this;
    },
    
    reinstateRejectsFromRange: function(range) {
        this.rejections.remove(range.radiators.models);
        return this;
    },
    
    render: function() {
        
        var data, compiled;
        
        this.suggestRadiators();
        
        data = this.model.toJSON();
        data.utils = HLC_Utils;
        data.radiator_button_anchor_text = HLC_Configs.radiator_button_anchor_text;
        data.radiator_button_title_text = HLC_Configs.radiator_button_title_text;
        
        data.app = HeatLossCalculator.app;
        
        data.radiator_combination = this.radiator_combination;
        
        data.radiators = this.suggestions.toJSON();
        
        _.each(data.radiators, function(radiator) {
             radiator.range = HeatLossCalculator.app.all_ranges.get(radiator.radiator_range_id).toJSON();
        });
        
        this.$el.html(this.template(data));
        
        return this;
    }
});